import React, { Fragment, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import './index.scss';

var LS_KEY = 'ant_fwp_list_w';

var loadListWidth = function (fallback) {
    try {
        var v = localStorage.getItem(LS_KEY);
        if (v) {
            var n = parseInt(v, 10);
            if (n >= 160 && n <= 600) return n;
        }
    } catch (e) { /* ignore */ }
    return fallback;
};

var saveListWidth = function (w) {
    try { localStorage.setItem(LS_KEY, String(w)); } catch (e) { /* ignore */ }
};

var FloatingWorkPanel = function (props) {
    var visible = props.visible;
    var onClose = props.onClose;
    var title = props.title;
    var icon = props.icon;
    var renderList = props.renderList;
    var renderDetail = props.renderDetail;
    var renderToolbar = props.renderToolbar;
    var listWidth = props.listWidth || 280;
    var minWidth = props.minWidth || 700;
    var maxWidth = props.maxWidth || 1100;
    var selectedItem = props.selectedItem;
    var onSelectItem = props.onSelectItem;
    var className = props.className;

    var [animating, setAnimating] = useState(false);
    var [show, setShow] = useState(false);
    var [currentListWidth, setCurrentListWidth] = useState(function () {
        return loadListWidth(listWidth);
    });

    var panelRef = useRef(null);
    var resizerRef = useRef(null);
    var dragState = useRef({ active: false, startX: 0, startW: 0 });

    // --- Visibility management with animation ---
    useEffect(function () {
        if (visible) {
            setShow(true);
            requestAnimationFrame(function () {
                setAnimating(true);
            });
        } else {
            setAnimating(false);
            var timer = setTimeout(function () {
                setShow(false);
            }, 200);
            return function () { clearTimeout(timer); };
        }
    }, [visible]);

    // --- Escape key handler ---
    useEffect(function () {
        if (!show) return;
        var handleKey = function (e) {
            if (e.key === 'Escape' && onClose) {
                onClose();
            }
        };
        document.addEventListener('keydown', handleKey);
        return function () { document.removeEventListener('keydown', handleKey); };
    }, [show, onClose]);

    // --- Backdrop click ---
    var handleOverlayClick = useCallback(function (e) {
        if (e.target === e.currentTarget && onClose) {
            onClose();
        }
    }, [onClose]);

    // --- Close button ---
    var handleClose = useCallback(function () {
        if (onClose) onClose();
    }, [onClose]);

    // --- Resizer drag ---
    var handleResizerMouseDown = useCallback(function (e) {
        e.preventDefault();
        dragState.current = { active: true, startX: e.clientX, startW: currentListWidth };
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    }, [currentListWidth]);

    useEffect(function () {
        var handleMouseMove = function (e) {
            if (!dragState.current.active) return;
            var diff = e.clientX - dragState.current.startX;
            var newW = dragState.current.startW + diff;
            if (newW < 160) newW = 160;
            if (newW > 600) newW = 600;
            setCurrentListWidth(newW);
        };
        var handleMouseUp = function () {
            if (!dragState.current.active) return;
            dragState.current.active = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            saveListWidth(currentListWidth);
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return function () {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [currentListWidth]);

    // --- Select item handler ---
    var handleSelectItem = useCallback(function (item) {
        if (onSelectItem) onSelectItem(item);
    }, [onSelectItem]);

    // --- Layout mode ---
    var hasList = typeof renderList === 'function';
    var hasDetail = typeof renderDetail === 'function';
    var isSinglePane = (!hasList || !hasDetail);

    // --- Panel style ---
    var panelStyle = useMemo(function () {
        var style = { minWidth: minWidth, maxWidth: maxWidth };
        if (isSinglePane) {
            style.minWidth = Math.min(minWidth, 500);
        }
        return style;
    }, [minWidth, maxWidth, isSinglePane]);

    // --- List style ---
    var listStyle = useMemo(function () {
        return { width: currentListWidth, minWidth: 160, maxWidth: 600 };
    }, [currentListWidth]);

    if (!show) return null;

    var overlayClass = 'fwp-overlay' + (animating ? ' fwp-overlay--active' : '');
    var panelClass = 'fwp-panel' + (animating ? ' fwp-panel--active' : '') + (className ? ' ' + className : '');

    var content = (
        <div className={overlayClass} onClick={handleOverlayClick}>
            <div className={panelClass} style={panelStyle} ref={panelRef}>
                {/* Header */}
                <div className="fwp-header">
                    <div className="fwp-header-left">
                        {icon ? (
                            <span className="fwp-header-icon">
                                {typeof icon === 'string' ? (
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d={icon} />
                                    </svg>
                                ) : icon}
                            </span>
                        ) : null}
                        <span className="fwp-header-title">{title || ''}</span>
                    </div>
                    <div className="fwp-header-right">
                        {renderToolbar ? (
                            <div className="fwp-header-toolbar">{renderToolbar()}</div>
                        ) : null}
                        <button className="fwp-close" onClick={handleClose} type="button" aria-label="Close">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                                <line x1="4" y1="4" x2="12" y2="12" />
                                <line x1="12" y1="4" x2="4" y2="12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="fwp-body">
                    {hasList && hasDetail ? (
                        <Fragment>
                            <div className="fwp-list" style={listStyle}>
                                {renderList(handleSelectItem)}
                            </div>
                            <div
                                className="fwp-resizer"
                                ref={resizerRef}
                                onMouseDown={handleResizerMouseDown}
                            />
                            <div className="fwp-detail">
                                {selectedItem ? renderDetail(selectedItem) : (
                                    <div className="fwp-empty" />
                                )}
                            </div>
                        </Fragment>
                    ) : hasList ? (
                        <div className="fwp-list fwp-list--full">
                            {renderList(handleSelectItem)}
                        </div>
                    ) : hasDetail ? (
                        <div className="fwp-detail fwp-detail--full">
                            {renderDetail(selectedItem)}
                        </div>
                    ) : (
                        <div className="fwp-empty" />
                    )}
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(content, document.body);
};

export default FloatingWorkPanel;
